import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import { get_api_url } from '../../../utils/urls';
import { api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

// Per-user preferences page (/cms-users-config). Visible to EVERY logged-in CMS
// user — no access_pages gate. The preferences are about the reminder invite
// emails THIS user receives as an assignee:
//   "Email reminders" (default on) — master switch; off → no invite email at all.
//   "Include calendar sync" (default on, subset of the above) — off → the
//   invite email is sent without the text/calendar part / invite.ics.
// Plus, independent of the reminder switch:
//   "Support inbox notifications" (default on) — off → no email when a support
//   ticket assigned to this user is updated or newly assigned to them.
export default function CmsUsersConfig() {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(true);
  const [includeCalendarSync, setIncludeCalendarSync] = useState(true);
  const [supportEmailNotificationsEnabled, setSupportEmailNotificationsEnabled] = useState(true);

  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  const error_message = (e) => {
    try {
      return e.response.data.message || e.response.data.reason;
    } catch (err) {
      return 'Some error occured. Please contact dev team.';
    }
  };

  const load_preferences = () => {
    setLoading(true);
    const url = get_api_url('/v2/cms_user/preferences', false, true);
    return api_with_method('get', url, null, true)
      .then((res) => {
        const data = res.data.data || {};
        // default checked when the field is absent (first-time user)
        setEmailRemindersEnabled(data.email_reminders_enabled !== false);
        setIncludeCalendarSync(data.include_calendar_sync !== false);
        setSupportEmailNotificationsEnabled(data.support_email_notifications_enabled !== false);
        setLoading(false);
      })
      .catch((e) => {
        notify('error', error_message(e));
        setLoading(false);
      });
  };

  useEffect(() => {
    load_preferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save_preferences = () => {
    setSaving(true);
    const url = get_api_url('/v2/cms_user/preferences', false, true);
    // Every flag is sent as a real boolean — unchecked reaches the server as
    // an explicit false (the backend upsert writes false, it never falls back
    // to the DB default).
    const payload = {
      email_reminders_enabled: emailRemindersEnabled,
      include_calendar_sync: includeCalendarSync,
      support_email_notifications_enabled: supportEmailNotificationsEnabled
    };
    api_with_method('put', url, payload, true)
      .then(() => {
        notify('success', 'Preferences saved');
        setSaving(false);
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSaving(false);
      });
  };

  const checkbox_style = { accentColor: 'var(--brand-primary)', width: 16, height: 16 };

  return (
    <CommonHeader loading={loading} title="My Preferences" class_name="cms-users-config-page">
      <div
        className="rounded-xl p-6"
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          maxWidth: 560
        }}
      >
        <div className="text-lg font-semibold mb-3">Reminder Notifications</div>

        <label
          className="flex items-center gap-3 cursor-pointer"
          style={{ color: 'var(--text-primary)' }}
        >
          <input
            type="checkbox"
            checked={emailRemindersEnabled}
            onChange={(e) => setEmailRemindersEnabled(e.target.checked)}
            style={checkbox_style}
          />
          <span className="text-sm">Email reminders</span>
        </label>

        {emailRemindersEnabled ? (
          <label
            className="flex items-center gap-3 cursor-pointer mt-2"
            style={{ color: 'var(--text-primary)', marginLeft: 28 }}
          >
            <input
              type="checkbox"
              checked={includeCalendarSync}
              onChange={(e) => setIncludeCalendarSync(e.target.checked)}
              style={checkbox_style}
            />
            <span className="text-sm">Include calendar sync</span>
          </label>
        ) : null}

        {/* Independent of the reminder switch above — support inbox emails are
            a separate opt-out, so this section is NOT nested under it. */}
        <div className="mt-5 pt-5" style={{ borderTop: '1px solid var(--border-default)' }}>
          <div className="text-lg font-semibold mb-3">Support Inbox Notifications</div>

          <label
            className="flex items-center gap-3 cursor-pointer"
            style={{ color: 'var(--text-primary)' }}
          >
            <input
              type="checkbox"
              checked={supportEmailNotificationsEnabled}
              onChange={(e) => setSupportEmailNotificationsEnabled(e.target.checked)}
              style={checkbox_style}
            />
            <span className="text-sm">Email me when a ticket assigned to me is updated</span>
          </label>
        </div>

        <div className="mt-4">
          <Button onClick={save_preferences} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </CommonHeader>
  );
}
